import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'

export type ToggleOption<T extends string> = {
    value: T
    label: string
    count?: number
}

interface ToggleGroupProps<T extends string> {
    label: string
    options: ToggleOption<T>[]
    value: T
    onChange: (value: T) => void
    variant?: 'pill' | 'underline' | 'chip'
    className?: string
}

export function ToggleGroup<T extends string>({
    label,
    options,
    value,
    onChange,
    variant = 'pill',
    className
}: ToggleGroupProps<T>): ReactNode {
    return (
        <div
            role='group'
            aria-label={label}
            className={cn(
                'no-scrollbar flex max-w-full overflow-x-auto',
                variant === 'pill' && 'inline-flex gap-1 rounded-lg bg-secondary p-1',
                variant === 'underline' && 'gap-1 border-b border-border',
                variant === 'chip' && 'flex-wrap gap-2 overflow-visible',
                className
            )}
        >
            {options.map(option => {
                const active = option.value === value

                return (
                    <button
                        key={option.value}
                        type='button'
                        aria-pressed={active}
                        data-active={active}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                            variant === 'pill' && 'h-8 rounded-md px-3 text-small text-muted-foreground hover:text-navy data-[active=true]:bg-card data-[active=true]:text-primary-deep data-[active=true]:shadow-card',
                            variant === 'underline' && '-mb-px h-11 border-b-2 border-transparent px-3 text-sm text-muted-foreground hover:border-input hover:text-navy data-[active=true]:border-primary data-[active=true]:text-primary-deep',
                            variant === 'chip' && 'h-9 rounded-full border border-input bg-card px-3.5 text-small text-foreground hover:border-primary/40 hover:text-primary-deep data-[active=true]:border-primary-strong data-[active=true]:bg-primary-strong data-[active=true]:text-white'
                        )}
                    >
                        <span>{option.label}</span>

                        {option.count !== undefined && (
                            <span
                                className={cn(
                                    'rounded-full px-1.5 text-caption tabular-nums',
                                    active && variant === 'chip' ? 'bg-white/20 text-white' : 'bg-secondary text-muted-foreground',
                                    active && variant !== 'chip' && 'bg-primary-soft text-primary-deep'
                                )}
                            >
                                {toFaDigits(option.count)}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
